import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Activity, AlertTriangle, Check, Copy, Shield, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/common/Logo";
import { supabase } from "@/lib/supabase";
import { detectInAppBrowser, copyCurrentUrl } from "@/lib/browser";
import { toast } from "sonner";

const REF_KEY = "super-pari-ref";

type Mode = "signin" | "signup";

export function Welcome() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [oauthLoading, setOauthLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const inApp = useMemo(() => detectInAppBrowser(), []);

  useEffect(() => {
    const ref = params.get("ref__");
    if (ref) localStorage.setItem(REF_KEY, ref);
  }, [params]);

  async function handleCopy() {
    const ok = await copyCurrentUrl();
    if (ok) {
      setCopied(true);
      toast.success("Lien copié");
      setTimeout(() => setCopied(false), 2500);
    } else {
      toast.error("Impossible de copier — copie l'URL manuellement");
    }
  }

  async function signInWithGoogle() {
    setOauthLoading(true);
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: `${window.location.origin}/auth/callback` },
    });
    if (error) {
      toast.error(error.message);
      setOauthLoading(false);
    }
  }

  function validate(): string | null {
    const cleanEmail = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)) {
      return t("auth.errors.emailInvalid");
    }
    if (password.length < 8) {
      return t("auth.errors.passwordTooShort");
    }
    return null;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setSubmitting(true);
    const cleanEmail = email.trim().toLowerCase();
    try {
      if (mode === "signup") {
        // Confirmation email désactivée côté dashboard → signUp retourne
        // une session directement (data.session non-null) et le user est
        // connecté. Sinon il faudrait gérer le mail de confirmation.
        const { data, error } = await supabase.auth.signUp({
          email: cleanEmail,
          password,
        });
        if (error) throw error;
        if (!data.session) {
          // Cas où la confirmation email est encore activée côté dashboard.
          toast.success("Compte créé. Vérifie tes emails pour activer.");
          setSubmitting(false);
          return;
        }
        navigate("/auth/onboarding", { replace: true });
      } else {
        const { error } = await supabase.auth.signInWithPassword({
          email: cleanEmail,
          password,
        });
        if (error) {
          // Supabase renvoie "Invalid login credentials" → on traduit.
          const msg = /invalid login/i.test(error.message)
            ? t("auth.errors.invalidCredentials")
            : error.message;
          throw new Error(msg);
        }
        navigate("/", { replace: true });
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erreur");
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen flex flex-col safe-top safe-bottom">
      <div className="flex flex-1 flex-col px-6 pt-10">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
        >
          <Logo size="md" />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 max-w-md"
        >
          <h1 className="text-[32px] leading-[1.05] font-bold tracking-tight">
            Le pronostic,{" "}
            <span className="text-primary">précis et premium.</span>
          </h1>
          <p className="mt-3 text-[14px] leading-relaxed text-muted-foreground">
            Suis les cotes en temps réel, place tes coupons et encaisse en mobile money.
          </p>
        </motion.div>

        <motion.ul
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="mt-6 space-y-2"
        >
          <Feature icon={Activity} text="Cotes & scores live" />
          <Feature icon={Zap} text="Mobile money instant · Wave · Orange · Free" />
          <Feature icon={Shield} text="Cagnotte hebdo TOP 100" />
        </motion.ul>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4, duration: 0.4 }}
        className="px-6 pb-8 pt-2 space-y-3"
      >
        {/* Tabs login / signup */}
        <div className="grid grid-cols-2 gap-0.5 p-0.5 rounded-lg bg-white/[0.04] border border-white/[0.06]">
          <ModeTab active={mode === "signin"} onClick={() => setMode("signin")}>
            {t("auth.signIn")}
          </ModeTab>
          <ModeTab active={mode === "signup"} onClick={() => setMode("signup")}>
            {t("auth.signUp")}
          </ModeTab>
        </div>

        <form onSubmit={handleSubmit} className="space-y-2.5">
          <div className="space-y-1.5">
            <Label htmlFor="email" className="text-xs">{t("auth.email")}</Label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              inputMode="email"
              placeholder="toi@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password" className="text-xs">{t("auth.password")}</Label>
            <Input
              id="password"
              type="password"
              autoComplete={mode === "signup" ? "new-password" : "current-password"}
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
            />
            {mode === "signup" && (
              <p className="text-2xs text-muted-foreground">{t("auth.passwordHint")}</p>
            )}
          </div>

          <Button type="submit" size="lg" className="w-full" loading={submitting}>
            {mode === "signup" ? t("auth.createAccount") : t("auth.signIn")}
          </Button>

          {mode === "signin" && (
            <div className="text-center">
              <Link
                to="/auth/forgot-password"
                className="text-2xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
              >
                {t("auth.forgotPassword")}
              </Link>
            </div>
          )}
        </form>

        {/* Séparateur "ou continue avec" */}
        <div className="flex items-center gap-2 py-1">
          <div className="flex-1 h-px bg-white/[0.06]" />
          <span className="text-[10px] text-muted-foreground uppercase tracking-wider">
            {t("auth.orContinueWith")}
          </span>
          <div className="flex-1 h-px bg-white/[0.06]" />
        </div>

        {inApp.isInApp && (
          <InAppBrowserWarning
            appName={inApp.appName}
            platform={inApp.platform}
            copied={copied}
            onCopy={handleCopy}
          />
        )}

        <Button
          onClick={signInWithGoogle}
          loading={oauthLoading}
          variant="outline"
          size="lg"
          className="w-full"
        >
          <GoogleIcon />
          Google
        </Button>

        <p className="text-center text-[10px] text-muted-foreground px-4 leading-relaxed pt-1">
          En continuant, tu acceptes nos{" "}
          <a href="/legal/terms" className="text-foreground/80 underline-offset-2 hover:underline">CGU</a>{" "}
          et notre{" "}
          <a href="/legal/privacy" className="text-foreground/80 underline-offset-2 hover:underline">Politique de confidentialité</a>.
        </p>
      </motion.div>
    </div>
  );
}

function ModeTab({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`h-8 text-xs font-medium transition-all duration-180 rounded-md ${
        active
          ? "bg-white/[0.08] text-foreground shadow-soft"
          : "text-muted-foreground hover:text-foreground"
      }`}
    >
      {children}
    </button>
  );
}

function InAppBrowserWarning({
  appName,
  platform,
  copied,
  onCopy,
}: {
  appName: string | null;
  platform: "ios" | "android" | "other";
  copied: boolean;
  onCopy: () => void;
}) {
  const source = appName ?? "ton navigateur intégré";
  const browserHint =
    platform === "ios"
      ? "Safari ou Chrome"
      : platform === "android"
        ? "Chrome ou Samsung Internet"
        : "Chrome / Safari / Firefox";
  const openInstruction =
    platform === "android"
      ? "Tape sur les 3 points (⋮) en haut, puis « Ouvrir dans Chrome »."
      : platform === "ios"
        ? "Tape sur l'icône Safari (carré avec flèche vers le haut) en bas, puis « Ouvrir dans Safari »."
        : "Ouvre ce lien dans un vrai navigateur Web.";

  return (
    <div className="rounded-xl border border-warning/30 bg-warning/[0.06] px-3.5 py-3 space-y-2">
      <div className="flex items-start gap-2.5">
        <AlertTriangle className="size-4 text-warning mt-0.5 shrink-0" />
        <div className="space-y-1">
          <p className="text-xs font-semibold text-warning">
            Google bloque la connexion dans {source}
          </p>
          <p className="text-2xs text-muted-foreground leading-relaxed">
            Ouvre ce lien dans <strong className="text-foreground">{browserHint}</strong>{" "}
            pour Google. {openInstruction}
          </p>
        </div>
      </div>
      <Button variant="outline" size="sm" onClick={onCopy} className="w-full">
        {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
        {copied ? "Lien copié" : "Copier le lien"}
      </Button>
    </div>
  );
}

function Feature({ icon: Icon, text }: { icon: React.ElementType; text: string }) {
  return (
    <li className="flex items-center gap-3 text-sm text-muted-foreground">
      <span className="flex size-7 items-center justify-center rounded-md bg-white/[0.04] border border-white/[0.06] text-primary">
        <Icon className="size-3.5" strokeWidth={2.25} />
      </span>
      <span>{text}</span>
    </li>
  );
}

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" className="size-4">
      <path fill="currentColor" d="M21.35 11.1H12v3.2h5.35c-.5 2.4-2.55 3.9-5.35 3.9-3.2 0-5.8-2.6-5.8-5.8s2.6-5.8 5.8-5.8c1.45 0 2.75.5 3.8 1.45L18.05 5.6C16.45 4.1 14.35 3.2 12 3.2 7.05 3.2 3 7.25 3 12.2s4.05 9 9 9c5.2 0 8.65-3.65 8.65-8.8 0-.55-.05-1-.15-1.3z" />
    </svg>
  );
}
